import { Box, Link, Tag, Text } from '@nimbus-ds/components';
import { EditIcon } from '@nimbus-ds/icons';
import { i18nResponseDto } from '@tiendanube/common';
import { CATALOG_UPLOAD_VIDEOS } from 'App/featuresFlags';
import { useHasTags } from 'App/hooks';
import { CardWithTitle, useResponsive } from 'commons/components';
import { useAiField } from 'commons/hooks';
import { AI_FIELDS } from 'commons/services/AgentChatService/AgentChatActionBusTypes';
import { useTranslationCatalog } from 'domains/Catalog/hooks';
import {
  ImageGalleryModal,
  AlertCloneStatus,
  AlertMediaStatus,
  MediaDropArea,
  UploadingMessage,
  RecommendationsGridRow,
  MediaPreview,
  Video,
} from './components';
import { AltTextGenerationContext } from './components/EditImageModal/EditImageActions/components/EditAltTextModal/components/AltTextGenerationModal';
import { useImageGallery } from './hooks';

export interface ImageGalleryState {
  isLoading: boolean;
  isError: boolean;
  id: string;
  src: string;
  alt: i18nResponseDto;
  name: string;
  file?: File;
  publicUrl?: string; // May be different from src if it's a freshly uploaded image using a blob as its src
  isEdited: boolean;
  replacedImageId?: string; // Used to store the id of the image that will be replaced by this one
  base64?: string; // Used to store the base64 of the image when it's edited and not uploaded yet
  type: string;
  size: number;
  mimeType: string;
  mediaType: 'video' | 'image';
  videoUrl: string;
}

export type MediaGalleryState = ImageGalleryState;

interface ImageGalleryProps {
  onClickVideo: () => void;
  onChange: (images: ImageGalleryState[], id?: string) => void;
  onChangeExternalVideoUrl: ({
    name,
    value,
  }: {
    name: string;
    value: string;
  }) => void;
  images: ImageGalleryState[];
  videoUrl?: string;
  altTextGenerationContext: AltTextGenerationContext;
  isEdit?: boolean;
  isFormSubmitted: boolean;
  errors: Partial<Record<string, string>>;
}

function ImageGallery({
  onClickVideo,
  onChange,
  onChangeExternalVideoUrl,
  images,
  videoUrl,
  altTextGenerationContext,
  isEdit,
  isFormSubmitted,
  errors,
}: Readonly<ImageGalleryProps>): JSX.Element {
  const t = useTranslationCatalog();
  const [hasUploadVideos] = useHasTags([CATALOG_UPLOAD_VIDEOS]);
  const { isDesktop } = useResponsive();

  const {
    message,
    videoQuantity,
    isShowModal,
    isOpenFileSystem,
    imageIdForEditOnOpen,
    handleOnchange,
    handleOnRemove,
    onRetryError,
    handleOnCloseModal,
    handleOnOpenModal,
    handleOnEdit,
    onOpenEditModal,
  } = useImageGallery({
    images,
    isEdit,
    onChange,
  });

  useAiField<string[]>({
    componentId: AI_FIELDS.PRODUCT_IMAGES,
    getValue: () => images.map((image) => image.publicUrl || image.src),
  });

  const title = hasUploadVideos
    ? t('products.detail.mediaPlural')
    : t('products.detail.media');

  return (
    <CardWithTitle
      title={title}
      type="h4"
      tag={
        hasUploadVideos && (
          <Tag id="video-beta-tag" appearance="primary">
            <Text
              color="primary-textLow"
              fontSize="caption"
              lineHeight="caption"
            >
              {t('products.detail.videoBetaTag')}
            </Text>
          </Tag>
        )
      }
    >
      <Box display="flex" flexDirection="column" gap="4">
        <AlertMediaStatus isFormSubmitted={isFormSubmitted} />
        <AlertCloneStatus />
        <MediaDropArea
          allowUploadVideos
          videoQuantity={videoQuantity}
          onUploadImages={handleOnchange}
          countItems={images.length}
        />
        <MediaPreview
          images={images}
          columns={isDesktop ? 4 : 3}
          maxImagesToPreview={isDesktop ? 4 : 3}
          onClickVideo={onClickVideo}
          onRemove={handleOnRemove}
          onEdit={handleOnEdit}
          openModal={handleOnOpenModal}
          onError={onRetryError}
        />
        {!!message && <UploadingMessage message={message} />}
        <RecommendationsGridRow allowUploadVideos />
        <Video
          videoUrl={videoUrl}
          errors={errors}
          onChange={onChangeExternalVideoUrl}
        />
        <Link appearance="primary" onClick={handleOnOpenModal}>
          <EditIcon />
          {hasUploadVideos
            ? t('products.detail.seeAllMedia')
            : t('products.detail.seeAllPhotos')}
        </Link>
      </Box>

      <ImageGalleryModal
        isShow={isShowModal}
        onClose={handleOnCloseModal}
        images={images}
        imageIdForEditOnOpen={imageIdForEditOnOpen}
        onOpenEditModal={onOpenEditModal}
        onChange={onChange}
        onError={onRetryError}
        isOpenFileSystem={isOpenFileSystem}
        onUploadImages={handleOnchange}
        onClickVideo={onClickVideo}
        message={message}
        altTextGenerationContext={altTextGenerationContext}
      />
    </CardWithTitle>
  );
}

export default ImageGallery;
